import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from "../../../hooks/useTranslation";
import { useUseCase } from "../../../hooks/useUseCase";
import { Container, Dialog, DialogActions, DialogButton, DialogTitle, makeStyles, Typography, useAuth, useDeclarativeLoading, } from "../../../index";
import { initialWorkSelectDialogState } from "./WorkSelectDialogState";
import { OpenWorkSelectDialogUseCase } from "./usecase/OpenWorkSelectDialogUseCase";
import { CloseWorkSelectDialogUseCase } from "./usecase/CloseWorkSelectDialogUseCase";
import { WorkSelectDialogToolbar } from "./components/WorkSelectDialogToolbar";
import { Work } from "../models/Work";
import { defaultFilterConditionObject, FilterCondition } from "../models/FilterCondition";
import { ChangeFilterUseCase } from "./usecase/ChangeFilterUseCase";
import { ChangePaginationUseCase } from "./usecase/ChangePaginationUseCase";
import { WorkSelectDialogContent } from "./components/WorkSelectDialogContent";
import { UnselectWorkUseCase } from "./usecase/UnselectWorkUseCase";
import { SelectWorkUseCase } from "./usecase/SelectWorkUseCase";
import { WorkUnavailableCourseDialog } from "./components/WorkUnavailableCourseDialog";
import { WorkUnavailableDialog } from "./components/WorkUnavailableDialog";
var useStyles = makeStyles(function (theme) { return ({
    root: {},
    container: {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        overflow: 'hidden',
        maxWidth: 'none',
        padding: 0,
    },
    actions: {
        position: 'relative',
    },
    count: {
        position: 'absolute',
        left: theme.spacing(3),
        top: '50%',
        transform: 'translate(0, -50%)',
    },
}); });
export var WorkSelectDialog = function (props) {
    var open = props.open, _a = props.defaultWork, defaultWork = _a === void 0 ? [] : _a, maxSelectableWorkNumber = props.maxSelectableWorkNumber, onSelect = props.onSelect, onCancel = props.onCancel;
    var user = useAuth().user;
    var _b = useUseCase(initialWorkSelectDialogState), state = _b.state, usecase = _b.usecase;
    var _c = useState(false), hasUnavailableCourseWork = _c[0], setHasUnavailableCourseWork = _c[1];
    var _d = useState(false), hasUnavailableWork = _d[0], setHasUnavailableWork = _d[1];
    var t = useTranslation().t;
    var classes = useStyles();
    useDeclarativeLoading('loading', state.isLoading);
    useEffect(function () {
        if (!user)
            return;
        open
            ? usecase(new OpenWorkSelectDialogUseCase()).execute({
                defaultWork: defaultWork.map(function (_defaultWork) { return Work.fromCallback(_defaultWork); }),
                userId: user.userId,
            })
            : usecase(new CloseWorkSelectDialogUseCase()).execute();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, user]);
    var handleFilterChange = useCallback(function (filterCondition) {
        if (!user)
            return;
        usecase(new ChangeFilterUseCase()).execute({ filterCondition: filterCondition, userId: user.userId });
    }, [usecase, user]);
    var handleFilterClear = useCallback(function () {
        if (!user)
            return;
        usecase(new ChangeFilterUseCase()).execute({
            filterCondition: new FilterCondition(defaultFilterConditionObject),
            userId: user.userId,
        });
    }, [usecase, user]);
    var handleChangePagination = useCallback(function (nextPage) {
        if (!user)
            return;
        usecase(new ChangePaginationUseCase()).execute({
            nextPage: nextPage,
            filterCondition: state.filterCondition,
            sortCondition: state.sortCondition,
            userId: user.userId,
        });
    }, [state.filterCondition, state.sortCondition, usecase, user]);
    var handleSelectWork = useCallback(function (work, selected) {
        selected
            ? usecase(new UnselectWorkUseCase()).execute({ work: work })
            : usecase(new SelectWorkUseCase()).execute({ work: work });
    }, [usecase]);
    var handleCancel = useCallback(function () {
        onCancel();
    }, [onCancel]);
    var handleSelect = useCallback(function () {
        if (state.selectedWorks.works.length === 0)
            return;
        // 教科書出版社設定由来で配付できないワークがないかどうかをチェック
        if (state.selectedUnavailableCourseWorkNames.length > 0) {
            setHasUnavailableCourseWork(true);
            return;
        }
        // 教材の削除に伴う不正状態によって配付できないワークがないかどうかチェック
        if (state.selectedUnavailableWorkNames.length > 0) {
            setHasUnavailableWork(true);
            return;
        }
        onSelect(state.selectedWorks.works.map(function (work) { return work.getWorkForCallback; }));
    }, [
        onSelect,
        state.selectedUnavailableCourseWorkNames.length,
        state.selectedWorks.works,
        state.selectedUnavailableWorkNames.length,
    ]);
    var handleCloseWorkUnavailableCourseDialog = function () {
        setHasUnavailableCourseWork(false);
    };
    var handleCloseWorkUnavailableDialog = function () {
        setHasUnavailableWork(false);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Dialog, { className: classes.root, open: open, fullScreen: true },
            React.createElement(DialogTitle, null, t('ワークを選ぶ')),
            React.createElement(Container, { className: classes.container },
                React.createElement(WorkSelectDialogToolbar, { onFilterChange: handleFilterChange, onFilterClear: handleFilterClear, filterCondition: state.filterCondition, isFilterEmpty: state.filterCondition.isEmpty, pagination: state.pagination, onChangePagination: handleChangePagination }),
                React.createElement(WorkSelectDialogContent, { works: state.works, onSelectWork: handleSelectWork, selectedWorkId: state.selectedWorkIds, selectable: state.selectedWorkIds.length < maxSelectableWorkNumber, sortCondition: state.sortCondition, onChangePagination: handleChangePagination, isLoading: state.isLoading, hasError: state.hasError })),
            React.createElement(DialogActions, { className: classes.actions },
                React.createElement(Typography, { className: classes.count }, t('選んだワーク') + " ",
                    React.createElement("span", { style: {
                            color: state.selectedWorkIds.length < maxSelectableWorkNumber ? '#4B4B4B' : '#F40000',
                        } }, state.selectedWorkIds.length), " / " + maxSelectableWorkNumber),
                React.createElement(DialogButton, { onClick: handleCancel }, t('キャンセル')),
                React.createElement(DialogButton, { fill: true, onClick: handleSelect, disabled: state.selectedWorks.works.length === 0 }, t('選ぶ')))),
        React.createElement(WorkUnavailableCourseDialog, { open: hasUnavailableCourseWork, workNames: state.selectedUnavailableCourseWorkNames, onClose: handleCloseWorkUnavailableCourseDialog }),
        React.createElement(WorkUnavailableDialog, { open: hasUnavailableWork, workNames: state.selectedUnavailableWorkNames, onClose: handleCloseWorkUnavailableDialog })));
};
